import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceDot,
  ResponsiveContainer,
  Tooltip as RTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { MouseHandlerDataParam } from 'recharts';
import type { BaselineSeriesPoint, EventFlagDay } from '@halo/web-types';
import type { Layer } from './Controls';
import { ChartTooltip } from './ChartTooltip';

interface BaselineChartProps {
  points: BaselineSeriesPoint[];
  eventDays: EventFlagDay[];
  layers: ReadonlySet<Layer>;
  onSelectDay: (date: string) => void;
}

const fmtUsd = (v: number) => `$${(v / 1000).toFixed(1)}k`;

export function BaselineChart({ points, eventDays, layers, onSelectDay }: BaselineChartProps) {
  const eventByDate = new Map(eventDays.map((d) => [d.date, d]));
  const data = points.map((p) => ({
    ...p,
    confidence: layers.has('confidence') ? p.baselineConfidence : undefined,
    pressure: layers.has('pressure') ? p.mediaPressureScore : undefined,
  }));
  const markedDays = layers.has('events')
    ? points.filter((p) => eventByDate.has(p.date) || p.isConfounded)
    : [];

  function handleClick(nextState: MouseHandlerDataParam) {
    const label = nextState.activeLabel;
    if (label !== undefined && label !== null) onSelectDay(String(label));
  }

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4">
      <h2 className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
        Actual vs baseline
      </h2>
      <ResponsiveContainer width="100%" height={320}>
        <ComposedChart data={data} onClick={handleClick}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 10, fill: '#9ca3af' }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            yAxisId="usd"
            tickFormatter={fmtUsd}
            tick={{ fontSize: 10, fill: '#9ca3af' }}
            axisLine={false}
            tickLine={false}
            width={52}
          />
          <YAxis yAxisId="score" orientation="right" domain={[0, 100]} hide />
          <RTooltip
            content={
              <ChartTooltip
                formatValue={(v, name) =>
                  name === 'confidence' || name === 'pressure'
                    ? v.toFixed(1)
                    : `$${v.toLocaleString()}`
                }
              />
            }
          />
          {layers.has('confidence') && (
            <Area
              yAxisId="score"
              dataKey="confidence"
              name="confidence"
              stroke="none"
              fill="#22c55e"
              fillOpacity={0.08}
              isAnimationActive={false}
            />
          )}
          {layers.has('pressure') && (
            <Line
              yAxisId="score"
              dataKey="pressure"
              name="pressure"
              stroke="#a855f7"
              strokeWidth={1}
              dot={false}
              isAnimationActive={false}
            />
          )}
          <Line
            yAxisId="usd"
            dataKey="actualRevenue"
            name="actual"
            stroke="#374151"
            strokeWidth={2}
            dot={false}
            isAnimationActive={false}
          />
          <Line
            yAxisId="usd"
            dataKey="selectedBaseline"
            name="selected baseline"
            stroke="#16a34a"
            strokeWidth={2}
            strokeDasharray="5 3"
            dot={false}
            isAnimationActive={false}
          />
          {layers.has('raw') && (
            <Line
              yAxisId="usd"
              dataKey="rawBaseline"
              name="raw baseline"
              stroke="#9ca3af"
              strokeWidth={1.5}
              strokeDasharray="2 3"
              dot={false}
              isAnimationActive={false}
            />
          )}
          {layers.has('adjusted') && (
            <Line
              yAxisId="usd"
              dataKey="mediaAdjustedBaseline"
              name="media-adjusted"
              stroke="#3b82f6"
              strokeWidth={1.5}
              strokeDasharray="4 2"
              dot={false}
              isAnimationActive={false}
            />
          )}
          {markedDays.map((p) => (
            <ReferenceDot
              key={p.date}
              x={p.date}
              y={0}
              yAxisId="usd"
              r={3}
              fill={p.isConfounded ? '#ef4444' : '#f59e0b'}
              stroke="none"
            />
          ))}
        </ComposedChart>
      </ResponsiveContainer>
      <p className="mt-1 text-[11px] text-gray-400">
        Click a day for detail. Red dots = confounded days, amber = downweighted event days.
      </p>
    </div>
  );
}
